import StaticPage from "@/components/StaticPage";

/**
 * Public SMS consent documentation for customers and Twilio toll-free reviewers.
 * Shows the exact opt-in language used in Studio and how STOP/HELP works.
 */
export default function MessagingConsent() {
  return (
    <StaticPage eyebrow="Messaging" title="SMS messaging consent" updatedAt="July 2026">
      <p className="text-[#4A4A4A]">
        NextVisit is operated by Brooke Tomlin doing business as NextVisit, a sole proprietorship.
        NextVisit helps appointment-based businesses send follow-up texts after a visit — thank-yous, review requests, and rebook reminders.
        We only text people who have consented. This page explains how that consent is collected and how to opt out.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Who texts are from</h2>
      <p>
        Texts are sent by Brooke Tomlin d/b/a NextVisit on behalf of the appointment business you visited.
        Messages identify the business and include instructions to reply <b>STOP</b> to opt out or <b>HELP</b> for help.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">How opt-in works</h2>
      <p>
        Consent is collected before any SMS is sent. Businesses record consent in NextVisit Studio when they add a customer
        (or when they import a customer list and attest that consent was already obtained). The SMS opt-in checkbox is
        <b> never pre-checked</b> and is separate from accepting our Terms or Privacy Policy.
      </p>

      <h3 className="font-heading text-xl text-[#183D36] mt-8 mb-2">Studio opt-in language (exact copy)</h3>
      <div
        className="rounded-2xl border border-[#183D36]/15 bg-white p-5"
        data-testid="messaging-consent-opt-in-demo"
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border border-[#183D36] bg-[#FAF7F0]"
          />
          <p className="text-sm text-[#252525] leading-relaxed">
            I confirm this customer agreed to receive SMS appointment follow-ups (thank-you, review request, and rebook reminders)
            from my business via NextVisit at the number provided. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.
            Consent is not a condition of purchase.{" "}
            <a href="/messaging-consent" className="text-[#183D36] underline">Messaging consent</a>
            {" · "}
            <a href="/privacy" className="text-[#183D36] underline">Privacy</a>
            {" · "}
            <a href="/terms" className="text-[#183D36] underline">Terms</a>
          </p>
        </div>
        <p className="text-xs text-[#4A4A4A] mt-4 font-accent uppercase tracking-widest">
          Checkbox appears only when a phone number is entered · unchecked by default
        </p>
      </div>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">What messages you may receive</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li>Thank-you after an appointment</li>
        <li>Review request</li>
        <li>Rebook / next-visit reminder</li>
        <li>Occasional win-back reminder if you haven&apos;t been in for a while</li>
      </ul>
      <p>
        Message frequency varies by the business&apos;s settings — typically a few messages around each visit, not daily marketing blasts.
        Msg &amp; data rates may apply.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Sample message</h2>
      <blockquote className="border-l-4 border-[#F4B183] pl-4 text-[#252525] italic">
        Hi Maya — thanks for visiting with us. Ready to book your next visit? Reply STOP to opt out. – NextVisit
      </blockquote>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">How to opt out</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li>Reply <b>STOP</b>, <b>STOPALL</b>, <b>UNSUBSCRIBE</b>, <b>CANCEL</b>, <b>END</b>, or <b>QUIT</b> to any NextVisit text.</li>
        <li>Reply <b>START</b>, <b>YES</b>, or <b>UNSTOP</b> to resubscribe.</li>
        <li>Reply <b>HELP</b> or <b>INFO</b> for help.</li>
      </ul>
      <p>
        Opt-out is honored immediately across NextVisit workspaces that have your number on file.
        You can also ask the business you booked with to remove you, or email{" "}
        <a className="text-[#183D36] underline" href="mailto:brooke@bookyournextvisit.com">brooke@bookyournextvisit.com</a>.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Related policies</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li><a className="text-[#183D36] underline" href="/privacy">Privacy Policy</a> — how we process phone numbers and message data</li>
        <li><a className="text-[#183D36] underline" href="/terms">Terms of Service</a> — business responsibilities for lawful consent</li>
      </ul>
    </StaticPage>
  );
}
